import React, { useRef, useEffect, useMemo, lazy, Suspense } from 'react';
import { Plus, LoaderPinwheel, MessageSquare, FileText, BarChart3 } from 'lucide-react';
import { ICON_SIZE } from '../../../constants/icons';
import { MiniShell } from './MiniShell';
import { MiniMacTitleBar } from './MiniMacTitleBar';
import { MiniTopbar } from './MiniTopbar';
import { MiniColumn, MiniColumnEmpty, MINI_DEMO_COLUMN_LAYOUT_CLASS } from './MiniColumn';
import { MiniColumnHeader } from './MiniColumnHeader';
import { MiniSidebar } from './MiniSidebar';
import { MiniSidebarFooterItem } from './MiniSidebarFooterItem';
import { MiniDivider } from './MiniDivider';
import { MiniSidebarFooter } from './MiniSidebarFooter';
import { MiniCursor } from './MiniCursor';
import { MiniModal } from './MiniModal';
import { MiniModalFooter, MiniIntegrationSourceButton, MiniComposeField } from './MiniModalFooter';
import { MiniConfirmation } from './MiniConfirmation';
import { useDemoEngine } from './useDemoEngine';
import type { Direction, DemoLayoutConfig } from './types';

import { RequirementCard } from '../app-demo/RequirementCard';
import { QuestionCard } from '../app-demo/QuestionCard';
import { AnswerCard } from '../app-demo/AnswerCard';
import { MiniGraphCard } from './MiniGraphCard';
import { buildDemoCoverageDimensions } from './demoCoverageDimensions';
import type { BuildDemoFlowGraphInput } from './buildDemoFlowGraph';
import type { Requirement, Question, Answer } from '../app-demo/types';

const MiniGraphView = lazy(() =>
  import('./MiniGraphView').then(module => ({ default: module.MiniGraphView })),
);

function resolveTarget(verb: string, subject: string): string {
  if (verb === 'browse') return 'ds-req-column';
  if (verb === 'open' && subject === 'import-modal') return 'ds-add-btn';
  if (verb === 'open' && subject === 'graph') return 'ds-summary';
  if (verb === 'import') return 'ds-modal-slack';
  if (verb === 'extract') return 'ds-modal-slack';
  if (verb === 'suggest') return 'ds-modal-slack';
  if (verb === 'select' && subject.includes('-r')) return `ds-modal-sug-${subject}`;
  if (verb === 'close' && subject === 'graph') return 'ds-graph-close';
  if (verb === 'close') return 'ds-req-column';
  if (verb === 'select') return `ds-req-${subject}`;
  if (verb === 'generate' && subject.startsWith('questions')) return 'ds-q-column';
  if (verb === 'generate' && subject === 'summary') return 'ds-summary';
  if (verb === 'accept') return `ds-q-${subject}`;
  if (verb === 'answer') return `ds-a-${subject}`;
  if (verb === 'export' && subject === 'linear') return 'ds-btn-linear';
  if (verb === 'export' && subject === 'cursor') return 'ds-btn-cursor';
  if (verb === 'delete') return `ds-req-${subject}`;
  return 'ds-req-column';
}

interface DemoShellProps {
  direction: Direction;
  layout: DemoLayoutConfig;
}

export function DemoShellView({ direction, layout }: DemoShellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const reqColumnRef = useRef<HTMLDivElement>(null);
  const qColumnRef = useRef<HTMLDivElement>(null);

  const { state, currentTransition, activeActor } = useDemoEngine(direction, containerRef);
  const pool = direction.contentPool;

  const allReqs = useMemo(() =>
    pool.requirements.filter(r => state.requirements.includes(r.id)) as Requirement[],
    [state.requirements, pool.requirements],
  );

  const questionIds = useMemo(
    () => (state.selectedRequirement ? (state.questions[state.selectedRequirement] ?? []) : []),
    [state.selectedRequirement, state.questions],
  );
  const allQuestions = useMemo(() => {
    const qPool = Object.values(pool.questions).flat() as Question[];
    return questionIds.map(id => qPool.find(q => q.id === id)).filter(Boolean) as Question[];
  }, [questionIds, pool.questions]);

  const answerIds = useMemo(
    () => (state.selectedQuestion ? (state.answers[state.selectedQuestion] ?? []) : []),
    [state.selectedQuestion, state.answers],
  );
  const allAnswers = useMemo(() => {
    const aPool = Object.values(pool.answers).flat() as Answer[];
    return answerIds.map(id => aPool.find(a => a.id === id)).filter(Boolean) as Answer[];
  }, [answerIds, pool.answers]);

  const selectedReq = allReqs.find(r => r.id === state.selectedRequirement);

  const coverageDimensions = useMemo(
    () => buildDemoCoverageDimensions(state.completeness),
    [state.completeness],
  );

  const flowAnswers = useMemo(() => {
    const aPool = Object.values(pool.answers).flat() as Answer[];
    const pairs: { answer: Answer; questionId: string }[] = [];
    for (const [questionId, answerIds] of Object.entries(state.answers)) {
      for (const answerId of answerIds) {
        const answer = aPool.find(a => a.id === answerId);
        if (answer) pairs.push({ answer, questionId });
      }
    }
    return pairs;
  }, [state.answers, pool.answers]);

  const graphQuestions = useMemo(
    () => allQuestions.filter(q => state.acceptedQuestions.includes(q.id)),
    [allQuestions, state.acceptedQuestions],
  );

  const graphInput = useMemo((): BuildDemoFlowGraphInput | null => {
    if (!selectedReq) return null;
    return {
      requirement: selectedReq,
      questions: graphQuestions,
      answers: flowAnswers,
      completeness: state.completeness,
      coverageDimensions,
      summaryGenerated: state.summaryGenerated,
      exports: state.exports,
    };
  }, [
    selectedReq,
    graphQuestions,
    flowAnswers,
    state.completeness,
    coverageDimensions,
    state.summaryGenerated,
    state.exports,
  ]);

  const cursorTarget = currentTransition
    ? resolveTarget(currentTransition.verb, currentTransition.subject)
    : null;

  useEffect(() => {
    if (currentTransition?.verb === 'browse' && currentTransition.subject === 'requirements' && reqColumnRef.current) {
      reqColumnRef.current.scrollTo({ top: 200, behavior: 'smooth' });
    }
  }, [currentTransition]);

  useEffect(() => {
    if (currentTransition?.verb === 'browse' && currentTransition.subject === 'questions' && qColumnRef.current) {
      qColumnRef.current.scrollTo({ top: 120, behavior: 'smooth' });
    }
  }, [currentTransition]);

  const showImportModal = state.modalPhase !== null && state.modalPhase !== 'selected';
  const isExtracting = state.modalPhase === 'importing' || state.modalPhase === 'extracting';
  const showSuggestions = state.modalPhase === 'suggestions' || state.modalPhase === 'selected';
  const isMacos = layout.shell.variant === 'macos';

  const hasReqColumn = layout.columns.some(c => c.key === 'requirements');
  const hasQColumn = layout.columns.some(c => c.key === 'questions');
  const hasAColumn = layout.showAnswers;
  const hasSummary = layout.showSummary;

  const modalFooter = layout.modal && state.modalPhase === 'open' ? (
    <MiniModalFooter
      left={(
        <>
          <MiniIntegrationSourceButton iconSrc="/figma.svg" label="Add Figma design links" />
          <MiniIntegrationSourceButton iconSrc="/slack.svg" label="Import from Slack" target="ds-modal-slack" />
          <MiniIntegrationSourceButton iconSrc="/gmail.svg" label="Import from Email" />
          <MiniIntegrationSourceButton iconSrc="/figma.svg" label="Upload files" />
        </>
      )}
      right={(
        <>
          <span className="text-[7px] text-text-quaternary px-2 py-1">Cancel</span>
          <span className="text-[7px] font-[var(--fw-medium)] text-text-primary px-2 py-1 rounded-comfortable bg-surface-frost-08">Submit</span>
        </>
      )}
    />
  ) : undefined;

  return (
    <div ref={containerRef} data-cursor-boundary={layout.boundaryId} className={layout.shell.containerClassName ?? 'absolute inset-0'}>
    <MiniShell
      visible
      shadow={layout.shell.shadow}
      roundedRight={layout.shell.roundedRight}
      roundedBottom={layout.shell.roundedBottom}
      variant={layout.shell.variant}
      className={layout.shell.className}
    >
      {isMacos && <MiniMacTitleBar title={layout.shell.title} />}

      <div className="flex flex-1 min-h-0 min-w-0">
        <MiniSidebar
          workspaceName={layout.workspace}
          teams={layout.sidebar.teams}
          expandedProjectId={layout.sidebar.expandedProjectId}
          footer={layout.sidebar.integrations ? (
            <>
              <MiniDivider />
              <MiniSidebarFooter>
                {layout.sidebar.integrations.map((int, i) => (
                  <React.Fragment key={int.label}>
                    {i > 0 && <MiniDivider />}
                    <MiniSidebarFooterItem icon={int.icon} label={int.label} isConnected={int.connected} value={int.value} />
                  </React.Fragment>
                ))}
              </MiniSidebarFooter>
            </>
          ) : undefined}
        />

        <div className="flex-1 flex flex-col min-w-0">
          <MiniTopbar segments={layout.breadcrumbs} />

          <div className="flex-1 flex min-h-0 overflow-hidden">
            {hasReqColumn && (() => {
              const col = layout.columns.find(c => c.key === 'requirements')!;
              return (
                <div className={`${MINI_DEMO_COLUMN_LAYOUT_CLASS} flex flex-col bg-surface-panel ${col.borderRight !== false ? 'border-r border-border-subtle' : ''}`}>
                  <MiniColumnHeader
                    title={col.title}
                    trailing={<div data-cursor-target="ds-add-btn"><Plus size={8} className="text-text-quaternary" /></div>}
                  />
                  <div ref={reqColumnRef} data-cursor-target="ds-req-column" className="flex-1 p-2 space-y-2 overflow-y-auto hide-scrollbar">
                    {allReqs.length > 0 ? (
                      allReqs.map(req => (
                        <div key={req.id} data-cursor-target={`ds-req-${req.id}`}>
                          <RequirementCard
                            req={req}
                            selected={state.selectedRequirement === req.id}
                            dimmed={!!state.selectedRequirement && state.selectedRequirement !== req.id}
                            visible
                          />
                        </div>
                      ))
                    ) : (
                      <MiniColumnEmpty icon={null} message="No requirements yet" />
                    )}
                  </div>
                </div>
              );
            })()}

            {hasQColumn && (() => {
              const col = layout.columns.find(c => c.key === 'questions')!;
              return (
                <div className={`${MINI_DEMO_COLUMN_LAYOUT_CLASS} flex flex-col bg-surface-panel ${col.borderRight !== false ? 'border-r border-border-subtle' : ''}`}>
                  <MiniColumnHeader
                    title={col.title}
                    trailing={state.selectedRequirement ? <LoaderPinwheel size={8} className="text-text-tertiary animate-spin" /> : undefined}
                  />
                  <div ref={qColumnRef} data-cursor-target="ds-q-column" className="flex-1 p-2 space-y-2 overflow-y-auto hide-scrollbar">
                    {state.selectedRequirement ? (
                      allQuestions.map(q => (
                        <div key={q.id} data-cursor-target={`ds-q-${q.id}`}>
                          <QuestionCard
                            q={q}
                            visible
                            suggested={!state.acceptedQuestions.includes(q.id)}
                            selected={state.selectedQuestion === q.id}
                          />
                        </div>
                      ))
                    ) : (
                      <MiniColumnEmpty icon={null} message="Select a requirement" />
                    )}
                  </div>
                </div>
              );
            })()}

            {hasAColumn && (
              <MiniColumn
                title="Answers"
                controls={state.selectedQuestion ? <Plus size={8} className="text-text-quaternary" /> : undefined}
              >
                {state.selectedQuestion ? (
                  allAnswers.map(a => (
                    <div key={a.id} data-cursor-target={`ds-a-${a.id}`}>
                      <AnswerCard answer={a} visible />
                    </div>
                  ))
                ) : (
                  <MiniColumnEmpty
                    icon={<MessageSquare size={ICON_SIZE.xs} className="text-text-quaternary opacity-20 mb-1" />}
                    message="Select a question"
                  />
                )}
              </MiniColumn>
            )}

            {hasSummary && (
              <MiniColumn
                title="Graph"
                borderRight={false}
                controls={state.summaryGenerated ? <BarChart3 size={8} className="text-text-quaternary" /> : undefined}
              >
                {state.summaryGenerated ? (
                  <>
                    <div data-cursor-target="ds-summary">
                      <MiniGraphCard
                        shortId="S01"
                        dimensions={coverageDimensions}
                        meanScore={state.completeness}
                      />
                    </div>
                    <div data-cursor-target="ds-btn-linear" />
                    <div data-cursor-target="ds-btn-cursor" />
                    {state.exports.includes('linear') && (
                      <MiniConfirmation visible icon="/linear.svg" message={`Ticket LIN-${100 + state.cycleCount} created`} />
                    )}
                    {state.exports.includes('cursor') && (
                      <MiniConfirmation visible icon="/cursor.svg" message="Agents started building" />
                    )}
                  </>
                ) : (
                  <MiniColumnEmpty
                    icon={<FileText size={ICON_SIZE.xs} className="text-text-quaternary opacity-20 mb-1" />}
                    message="Select a requirement"
                  />
                )}
              </MiniColumn>
            )}
          </div>
        </div>
      </div>

      {layout.modal && (
        <MiniModal visible={showImportModal} title={layout.modal.title} footer={modalFooter}>
          <div className="space-y-2">
            {state.modalPhase === 'open' && (
              <div className="space-y-2">
                <MiniComposeField
                  label="Title"
                  placeholder={layout.modal.composeTitlePlaceholder ?? 'Requirement title'}
                />
                <MiniComposeField
                  label="Description"
                  placeholder={layout.modal.composeDescriptionPlaceholder ?? 'Describe the requirement...'}
                />
              </div>
            )}

            {isExtracting && (
              <div className="flex flex-col items-center py-4 space-y-2">
                <LoaderPinwheel size={ICON_SIZE.sm} className="text-text-tertiary animate-spin" />
                <p className="text-[7px] text-text-tertiary">{layout.modal.extractingMessage}</p>
              </div>
            )}

            {showSuggestions && pool.slackSuggestions?.map(sug => (
              <div
                key={sug.id}
                data-cursor-target={`ds-modal-sug-${sug.id}`}
                className={`flex items-center justify-between px-2 py-1.5 rounded-micro border transition-all duration-300 ${
                  state.requirements.includes(sug.id)
                    ? 'bg-surface-frost-05 border-border-default'
                    : 'bg-surface-elevated border-border-subtle'
                }`}
              >
                <div className="min-w-0">
                  <div className="text-[7px] font-[var(--fw-medium)] text-text-primary truncate">{sug.text}</div>
                  <div className="text-[6px] text-text-quaternary">{sug.source}</div>
                </div>
              </div>
            ))}
          </div>
        </MiniModal>
      )}

      {graphInput && state.graphOpen && (
        <Suspense fallback={null}>
          <MiniGraphView graphInput={graphInput} visible={state.graphOpen} />
        </Suspense>
      )}
    </MiniShell>

    {direction.actors.map(actor => (
      <MiniCursor
        key={actor.id}
        name={actor.name}
        target={activeActor === actor.id && cursorTarget ? cursorTarget : ''}
        visible={activeActor === actor.id && !!cursorTarget}
        boundaryId={layout.boundaryId}
      />
    ))}
    </div>
  );
}
